/**
 * Product Master — รูปร่างข้อมูลตาม payload contract ที่ freeze แล้ว
 * (`docs/API_CONTRACT.md` — "Product Master — Payload Contract")
 *
 * ไฟล์นี้เป็นภาพสะท้อนของเอกสารนั้นเท่านั้น ไม่ใช่ที่ตัดสินใจเพิ่มเติมเอง
 * ถ้าเซิร์ฟเวอร์ (`WO-P1-M3-001`) กับที่นี่ไม่ตรงกัน ให้แก้ที่ contract ก่อน
 * แล้วทั้งสองฝั่งค่อยตามเอกสาร — ห้ามฝ่ายใดฝ่ายหนึ่งขยับเอง
 *
 * ⚠️ ตัวเลข `NUMERIC` (`safety_stock`, `unit_cost`) เป็น **string** ทั้งขาเข้า
 * และขาออก เพราะ `NUMERIC(18,4)` เก็บใน IEEE-754 double ไม่ครบ ห้ามแปลงผ่าน
 * `Number`/`parseFloat` ไม่ว่าจะเพื่อแสดงผลหรือเพื่อส่งกลับ
 */

import type { PermissionAction } from '../permissions/types';

/**
 * รหัสโมดูลของกลุ่มนี้ใน RBAC matrix — ค่าเดียวกับที่ทุก endpoint ฝั่ง API ส่งให้
 * `require_permission("PRODUCT_MASTER", ...)` (`docs/API_CONTRACT.md`)
 */
export const PRODUCT_MASTER_MODULE = 'PRODUCT_MASTER';

/** สถานะตาม D-011 — ใช้เป็น "ตัวเลือกที่รู้จัก" ไม่ใช่ตัวกรองความถูกต้องขาเข้า */
export const PRODUCT_STATUSES = [
  'DRAFT',
  'PENDING_APPROVAL',
  'ACTIVE',
  'REJECTED',
  'INACTIVE',
] as const;

/**
 * โหมดเติมเต็มที่ "เลือกได้" ตอนสร้าง — Phase 1 มีแต่ PUSH (D-034)
 *
 * ค่า `PULL` ยังคงแสดงได้ถ้าเซิร์ฟเวอร์ส่งมา (ดู `ProductPlantData`) —
 * การแสดงผลกับการรับ input เป็นคนละปัญหากัน หน้าจอที่ crash เพราะเจอค่าที่
 * ตัวเองเลือกให้ไม่ได้ คือหน้าจอที่ไว้ใจไม่ได้
 */
export const CREATABLE_REPLENISHMENT_MODES = ['PUSH'] as const;

export type CreatableReplenishmentMode = (typeof CREATABLE_REPLENISHMENT_MODES)[number];

/**
 * ฟิลด์ที่เซิร์ฟเวอร์เป็นผู้เติมและ client ส่งมาไม่ได้เลย
 * (`docs/API_CONTRACT.md` — ส่งมาให้ตอบ `400`)
 *
 * ห้าเป็นฟิลด์ผู้กระทำจาก `WO-P1-M4A-003` ที่เหลือคือสถานะและ timestamp ของแถว
 * รายการนี้มีไว้ให้ทั้งหน้าจอ (แสดง read-only) และเทสต์ (พิสูจน์ว่าไม่ได้ส่งออก)
 * อ้างชุดเดียวกัน จะได้ไม่มีวันเลื่อนออกจากกัน
 */
export const SERVER_OWNED_PRODUCT_FIELDS = [
  'product_id',
  'status',
  'created_by_employee_id',
  'submitted_by_employee_id',
  'submitted_at',
  'approved_by_employee_id',
  'approved_at',
  'created_at',
  'updated_at',
] as const;

/** ฟิลด์ผู้กระทำทั้งห้าที่หน้ารายละเอียดต้องแสดงเป็น read-only (D-010) */
export const PRODUCT_AUTHORSHIP_FIELDS = [
  'created_by_employee_id',
  'submitted_by_employee_id',
  'submitted_at',
  'approved_by_employee_id',
  'approved_at',
] as const;

export type ProductAuthorshipField = (typeof PRODUCT_AUTHORSHIP_FIELDS)[number];

/**
 * ข้อมูลระดับ Plant ของ product หนึ่งตัว
 *
 * เซิร์ฟเวอร์คืนเฉพาะ Plant ที่ผู้เรียกมีสิทธิ์เห็น (`get_current_user_plant_scope`,
 * D-004) — product ตัวเดียวกันจึงคืนรายการไม่เท่ากันสำหรับคนละผู้ใช้ และนั่นถูกต้อง
 * หน้าจอต้องพูดว่า "โรงงานที่คุณเห็น" ไม่ใช่ "ทุกโรงงานที่มี product นี้"
 */
export interface ProductPlantData {
  readonly plant_id: string;
  readonly plant_code: string;
  /** `PUSH`/`PULL` — เก็บเป็น string เพื่อให้ค่าที่ยังไม่รู้จักแสดงได้โดยไม่พัง */
  readonly default_replenishment_mode: string;
  readonly lot_size_procedure: string | null;
  readonly planned_delivery_time: number | null;
  /** `NUMERIC` — string เสมอ ห้ามแปลงเป็น number */
  readonly safety_stock: string | null;
  /** `NUMERIC(18,4)` — string เสมอ ห้ามแปลงเป็น number */
  readonly unit_cost: string | null;
  readonly valuation_class: string | null;
}

/** Resource shape เต็มตาม contract */
export interface Product {
  readonly product_id: string;
  readonly product_code: string;
  readonly name: string;
  /**
   * รุ่น/โมเดลของสินค้าตามที่ลูกค้ากำหนด (D-107 ข้อ 1) — ข้อความอิสระ, optional
   * ทุกที่ที่ปรากฏ `null` = ยังไม่มีใครบันทึกไว้ ไม่ใช่ข้อผิดพลาด และสองสินค้า
   * มีรุ่นเดียวกันได้ (เลือกใช้ free text แทน controlled list เมื่อ 2026-09-17)
   */
  readonly model: string | null;
  readonly product_type: string;
  readonly base_uom: string;
  /** น้ำหนักต่อชิ้น (กก.) `NUMERIC(12,4)` — string เสมอ; `null` = ยังไม่ระบุ (D-165) */
  readonly kg_per_piece: string | null;
  /** เกรดเหล็ก; ชุดเหล็ก 5 ช่องนี้ครบหรือว่างทั้งชุดเสมอ (D-165) */
  readonly material_spec: string | null;
  /** `SHEET` | `COIL` — เก็บเป็น string เพื่อให้ค่าที่ยังไม่รู้จักแสดงได้ */
  readonly material_form: string | null;
  /** ความหนา มม. (3 ตำแหน่ง) — string เสมอ */
  readonly thickness_mm: string | null;
  /** ความกว้าง มม. (2 ตำแหน่ง) — string เสมอ */
  readonly width_mm: string | null;
  /** ความยาว มม. — มีเฉพาะ `SHEET`; `COIL` เป็น `null` เสมอ */
  readonly length_mm: string | null;
  /** เก็บเป็น string: สถานะที่ยังไม่รู้จักต้องแสดงได้ ไม่ใช่ทำให้หน้าจอพัง */
  readonly status: string;
  readonly created_by_employee_id: string | null;
  readonly submitted_by_employee_id: string | null;
  readonly submitted_at: string | null;
  readonly approved_by_employee_id: string | null;
  readonly approved_at: string | null;
  readonly created_at: string;
  readonly updated_at: string;
  readonly plant_data: readonly ProductPlantData[];
}

/**
 * `GET /v1/products` — envelope แบบ cursor
 *
 * ใช้ cursor ไม่ใช่ offset เพราะ offset จะข้าม/ซ้ำแถวเมื่อมีการแก้ไขระหว่างเปิดหน้า
 * `next_cursor === null` แปลว่าไม่มีหน้าถัดไป ไม่ใช่ "ไม่มีข้อมูล"
 */
export interface ProductListPage {
  readonly items: readonly Product[];
  readonly next_cursor: string | null;
}

/** Query ที่ `GET /v1/products` รองรับ */
export interface ProductListFilters {
  readonly plant_code?: string;
  readonly product_type?: string;
  readonly status?: string;
  /** กรองย่อยจาก `model` (substring, ไม่สนตัวพิมพ์) — คนละตัวกับ `q` ที่ค้นกว้าง (D-134) */
  readonly model?: string;
  readonly default_replenishment_mode?: string;
  readonly q?: string;
  readonly limit?: number;
  readonly cursor?: string;
}

/**
 * `plant_data` หนึ่งรายการใน body ของ `POST /v1/products`
 *
 * `default_replenishment_mode` ที่ส่งได้มีแต่ `PUSH` โดยชนิดข้อมูล — Phase 1
 * ยังไม่มี Pull flow และ contract กำหนดให้ `PULL` ถูกปฏิเสธด้วย `422`
 * แบบ feature-not-enabled ไม่ใช่ `400` unknown value (D-034)
 */
export interface CreateProductPlantInput {
  readonly plant_code: string;
  readonly valuation_class?: string;
  readonly planned_delivery_time?: number;
  readonly lot_size_procedure?: string;
  /** `NUMERIC` — ส่งเป็น string ที่ผู้ใช้พิมพ์ ห้าม normalize ผ่าน number */
  readonly safety_stock?: string;
  /** `NUMERIC(18,4)` — string เท่านั้นด้วยเหตุผลเดียวกัน */
  readonly unit_cost?: string;
  readonly default_replenishment_mode?: CreatableReplenishmentMode;
}


/**
 * น้ำหนักต่อชิ้นและคุณสมบัติเหล็ก (D-165) — ทุกช่องเป็น string ที่ผู้ใช้พิมพ์
 * (เซิร์ฟเวอร์ปฏิเสธ JSON float) ชุดเหล็ก 5 ช่อง **ครบหรือว่างทั้งชุด** ส่วน `kg_per_piece`
 * อยู่นอกชุด; `SHEET` ต้องมี `length_mm`, `COIL` ห้ามมี
 */
export const PRODUCT_STEEL_FIELDS = [
  'kg_per_piece',
  'material_spec',
  'material_form',
  'thickness_mm',
  'width_mm',
  'length_mm',
] as const;

export type ProductSteelField = (typeof PRODUCT_STEEL_FIELDS)[number];

export const PRODUCT_MATERIAL_FORMS = ['SHEET', 'COIL'] as const;

export type ProductMaterialForm = (typeof PRODUCT_MATERIAL_FORMS)[number];

/**
 * Body ของ `POST /v1/products`
 *
 * ไม่มี `status` และไม่มีฟิลด์ผู้กระทำโดยชนิดข้อมูล — นั่นคือด่านแรก
 * ด่านที่สองคือ `serializeCreateProductBody()` ที่หยิบเฉพาะคีย์ที่รู้จักออกไป
 */
export interface CreateProductBody {
  readonly product_code: string;
  readonly name: string;
  /** Optional: ไม่ระบุ = ไม่ส่ง key นี้เลย เซิร์ฟเวอร์เก็บเป็น NULL */
  readonly model?: string;
  readonly product_type: string;
  readonly base_uom: string;
  /** ไม่ระบุ = ไม่ส่ง key นั้นเลย (D-165) */
  readonly kg_per_piece?: string;
  readonly material_spec?: string;
  readonly material_form?: ProductMaterialForm;
  readonly thickness_mm?: string;
  readonly width_mm?: string;
  readonly length_mm?: string;
  readonly plant_data: readonly CreateProductPlantInput[];
}

/* ------------------------------------------------------------- edit ------ */

/**
 * สถานะเดียวที่ `PATCH /v1/products/{product_id}` แก้ได้ (ตาราง endpoint + D-011)
 *
 * แยกจาก `PRODUCT_TRANSITION_RULES.submit.source` ที่บังเอิญเป็นค่าเดียวกัน เพราะ
 * เป็นคนละกฎกัน: ข้อนี้มาจากบรรทัด `PATCH` ในตาราง endpoint ส่วนข้อนั้นมาจาก
 * state machine ของ workflow ถ้าวันหนึ่งข้อใดข้อหนึ่งเปลี่ยน การใช้ค่าร่วมกันจะ
 * ทำให้อีกข้อเปลี่ยนตามไปเงียบ ๆ
 */
export const PRODUCT_EDITABLE_STATUS = 'DRAFT';

/**
 * `details.rule` ของ `422` เมื่อ record พ้น `DRAFT` ไปแล้ว
 *
 * ความหมายของมันไม่ใช่ "ห้ามแก้" แต่คือ "แก้ทางนี้ไม่ได้แล้ว ต้องไปทาง
 * `product_change_request`" (D-011/D-042) — และเส้นทางนั้น **มีหน้าจอแล้ว**
 * (`ProductChangeRequestView`) หน้าจอที่แปลรหัสนี้เป็นทางตันจึงไม่ถูกต้องอีกต่อไป
 * เช่นเดียวกับปุ่มที่กดแล้วไม่ไปไหน — ทั้งสองแบบสอนให้ผู้ใช้เชื่อว่าระบบเสีย
 */
export const PRODUCT_EDITABLE_ONLY_IN_DRAFT = 'PRODUCT_EDITABLE_ONLY_IN_DRAFT';

/**
 * `plant_data` หนึ่งรายการใน body ของ `PATCH /v1/products/{product_id}`
 *
 * ต่างจากตอนสร้างสองข้อ และทั้งสองข้อมาจากฝั่งเซิร์ฟเวอร์ตรง ๆ:
 *
 * 1. **รายการที่ส่งไปไม่ได้แทนที่ของเดิมทั้งชุด** เซิร์ฟเวอร์ apply ทีละรายการโดยจับคู่
 *    ด้วย `plant_code` โรงงานที่ไม่ได้เอ่ยถึงจึงไม่ถูกแตะ — จำเป็น เพราะผู้เรียกเห็น
 *    เฉพาะโรงงานใน scope ของตัวเอง (D-004) การส่งรายการที่เห็นไปเป็น "ของใหม่ทั้งชุด"
 *    จะลบมุมมองของโรงงานที่ตัวเองมองไม่เห็นทิ้ง
 * 2. **`null` แปลว่า "ล้างค่า" ส่วนการไม่ส่งคีย์แปลว่า "ไม่แตะ"** สองอย่างนี้ต่างกัน
 *    จริงสำหรับฟิลด์ที่ nullable ได้ จึงต้องเขียนแยกกันในชนิดข้อมูล
 *
 * `valuation_class` ไม่มี `null` เพราะคอลัมน์เป็น `NOT NULL` (เซิร์ฟเวอร์ปฏิเสธ
 * `null` ตรง ๆ) และ `default_replenishment_mode` รับได้เฉพาะ `PUSH` ด้วยเหตุผล
 * เดียวกับตอนสร้าง (D-034) — ชนิดข้อมูลจึงไม่เปิดทางให้หน้าจอส่ง `PULL` ออกไปได้เลย
 */
export interface UpdateProductPlantInput {
  /** คีย์ที่ใช้จับคู่กับมุมมองโรงงานที่มีอยู่ — ไม่ใช่ค่าที่แก้ได้ */
  readonly plant_code: string;
  readonly valuation_class?: string;
  readonly planned_delivery_time?: number | null;
  readonly lot_size_procedure?: string | null;
  /** `NUMERIC` — string ที่ผู้ใช้พิมพ์ หรือ `null` เพื่อล้างค่า ห้ามผ่าน number */
  readonly safety_stock?: string | null;
  /** `NUMERIC(18,4)` — string หรือ `null` ด้วยเหตุผลเดียวกัน */
  readonly unit_cost?: string | null;
  readonly default_replenishment_mode?: CreatableReplenishmentMode;
}

/**
 * Body ของ `PATCH /v1/products/{product_id}` — สี่คีย์ และไม่มีคีย์ที่ห้า
 *
 * **ไม่มี `product_code`** เพราะรหัสธุรกิจของ record ที่สร้างแล้วเปลี่ยนไม่ได้
 * (contract ระบุไว้ตรง ๆ) ไม่มี `status` เพราะสถานะขยับผ่าน transition เท่านั้น
 * และไม่มีฟิลด์ผู้กระทำทั้งห้าด้วยเหตุผลเดียวกับตอนสร้าง (D-010)
 *
 * **ไม่มี `expected_status`** เช่นกัน และนั่นเป็นการตัดสินใจ ไม่ใช่การลืม:
 * contract ระบุว่า `PATCH` บน `DRAFT` ยอมรับ last-write-wins ได้เพราะยังไม่ผ่าน
 * การอนุมัติและแก้ซ้ำได้ ส่วน guard แบบ `expected_status` สงวนไว้ให้ transition
 * ซึ่งเป็นจุดที่ stale view อันตรายจริง การเติมเข้ามาเองที่นี่คือการสร้าง
 * optimistic-concurrency check ที่เซิร์ฟเวอร์ไม่ได้อ่าน
 */
export interface UpdateProductBody {
  readonly name?: string;
  /**
   * Optional และ nullable — **สามค่าที่เป็นไปได้มีความหมายต่างกันทั้งหมด**
   * (`docs/API_CONTRACT.md`, `schemas.py` ฝั่ง `ProductPatchRequest.model`):
   * ไม่ระบุ key นี้เลย = "ไม่แตะ", `null` = "ล้างค่า" (รุ่น/โมเดลที่เคยบันทึกไว้
   * ถูกถอนออก), string = "ตั้งค่าใหม่" — ฟอร์มที่ส่ง `""` แทน `null` จะไปชนขอบเขต
   * `min_length=1` ของคอลัมน์ฝั่งเซิร์ฟเวอร์แทนที่จะล้างค่าได้จริง
   */
  readonly model?: string | null;
  readonly product_type?: string;
  readonly base_uom?: string;
  /** D-165: ไม่ส่ง key = ไม่แตะ, `null` = ล้างค่า; ชุดเหล็กตัดสินจากค่าหลังรวมกับของเดิม */
  readonly kg_per_piece?: string | null;
  readonly material_spec?: string | null;
  readonly material_form?: ProductMaterialForm | null;
  readonly thickness_mm?: string | null;
  readonly width_mm?: string | null;
  readonly length_mm?: string | null;
  readonly plant_data?: readonly UpdateProductPlantInput[];
}

/* ------------------------------------------------ transition (workflow) -- */

/**
 * Transition ทั้งสามที่ contract freeze ไว้ — **ไม่มีตัวที่สี่**
 *
 * ค่าในรายการนี้คือ path segment จริงของ endpoint (`.../submit`) ด้วย จึงไม่มี
 * ตารางแปลงชื่อที่เลื่อนออกจาก URL ได้ ส่วนคำขอแก้ไข (`change-requests`) และ
 * การปิดใช้งานตาม D-042 อยู่ในหัวข้อ change request ท้ายไฟล์ เพราะเป็นคนละ
 * resource กัน ไม่ใช่ transition ตัวที่สี่ของ product
 */
export const PRODUCT_TRANSITIONS = ['submit', 'approve', 'reject'] as const;

export type ProductTransition = (typeof PRODUCT_TRANSITIONS)[number];

/**
 * เงื่อนไขของแต่ละ transition ตามตาราง endpoint + หัวข้อ transition ใน contract
 *
 * `source` คือสถานะเดียวที่ transition นั้นทำได้ และ `action` คือสิทธิ์ที่ตาราง
 * endpoint กำหนด (EDIT สำหรับ submit, APPROVE สำหรับอีกสองตัว) — หน้าจอใช้ทั้งคู่
 * เพื่อ "ไม่เสนอคำสั่งที่ยังไงก็ล้มเหลว" เท่านั้น **ไม่ใช่เพื่อกันไม่ให้เกิดขึ้น**
 * เซิร์ฟเวอร์ตรวจทั้งสองข้อนี้เองทุกคำขอ (`_require_expected_status` +
 * `require_permission`) การซ่อนปุ่มจึงเป็นความสะดวก ไม่ใช่ด่าน
 */
export interface ProductTransitionRule {
  readonly source: string;
  readonly action: PermissionAction;
}

export const PRODUCT_TRANSITION_RULES: Readonly<
  Record<ProductTransition, ProductTransitionRule>
> = {
  submit: { source: 'DRAFT', action: 'EDIT' },
  approve: { source: 'PENDING_APPROVAL', action: 'APPROVE' },
  reject: { source: 'PENDING_APPROVAL', action: 'APPROVE' },
};

/**
 * Body ของทั้งสาม endpoint — คีย์เดียว และ **บังคับ**
 *
 * ค่าที่ส่งต้องเป็นสถานะที่หน้าจอ "กำลังแสดงอยู่จริง" ไม่ใช่สถานะที่โค้ดคิดว่าควรจะเป็น
 * นี่คือทั้งหมดของ stale-view guard: ถ้าคนอื่นขยับ record ไปแล้วระหว่างที่หน้านี้
 * เปิดค้าง เซิร์ฟเวอร์ต้องตอบ `409` แทนที่จะทำตาม
 */
export interface ProductTransitionBody {
  readonly expected_status: string;
}

/**
 * `details.rule` ของ segregation of duties (D-010)
 *
 * ประกาศเป็นค่าคงที่เพื่อให้หน้าจอ เทสต์ และคำอธิบายบนจอ อ้างสตริงเดียวกัน —
 * กฎข้อนี้คือเหตุผลที่ workflow อนุมัติมีอยู่ ผู้ใช้ที่ถูกปฏิเสธต้องอ่านออกว่า
 * "คนสร้างอนุมัติเองไม่ได้" ไม่ใช่ "ทำรายการไม่สำเร็จ"
 *
 * ⚠️ รหัสเดียวกันนี้ถูกใช้กับ **สอง** resource ที่คนละเจ้าของกัน: ของ product
 * หมายถึงผู้สร้าง product ส่วนของ change request หมายถึง **ผู้ยื่นคำขอนั้น**
 * (contract: "`created_by_employee_id` ของ **change request** นั้น") หน้าจอจึง
 * ต้องอธิบายคนละแบบตามบริบท ไม่ใช่ประโยคเดียวใช้ทั้งคู่
 */
export const APPROVER_MUST_DIFFER_FROM_CREATOR = 'APPROVER_MUST_DIFFER_FROM_CREATOR';

/* -------------------------------------------------- change request ------- */

/**
 * คำขอเปลี่ยนแปลงข้อมูลสินค้า (`product_change_request`, D-011/D-042)
 *
 * เหตุผลที่กลไกนี้มีอยู่ (contract, หัวข้อ "Product Change Request"): record ที่
 * `ACTIVE` ต้องใช้งานได้ตลอดเวลาแม้มีข้อเสนอแก้ค้างอยู่ เพราะ BOM, Routing และ
 * Production Order อ้างถึงมันอยู่ ข้อเสนอจึงอยู่ในตารางแยก และ `status` ของแถว
 * เดิมไม่ถูกใช้ track การแก้ที่รออนุมัติ
 *
 * ทุกชนิดข้อมูลด้านล่างเป็นภาพสะท้อนของ contract ที่ freeze ไว้เท่านั้น ฝั่ง API
 * กำลังถูกสร้างขนานกันจากเอกสารฉบับเดียวกัน — ถ้าสองฝั่งไม่ตรงกัน ให้แก้เอกสาร
 * ก่อน ห้ามฝ่ายใดฝ่ายหนึ่งขยับเอง
 */

/**
 * ชนิดของคำขอ — มีสองค่าเท่านั้น (D-042) และ **ไม่มี endpoint `/deactivate` แยก**
 *
 * `DEACTIVATE` มีรูปร่างต่างออกไปโดยกติกา ไม่ใช่โดยบังเอิญ: `proposed_changes`
 * ต้องว่าง และ `plant_code` ต้องเป็น `null` เสมอ เพราะการปิด product เป็นการ
 * ตัดสินระดับ record ไม่ใช่ระดับ Plant
 */
export const PRODUCT_CHANGE_TYPES = ['FIELD_UPDATE', 'DEACTIVATE'] as const;

export type ProductChangeType = (typeof PRODUCT_CHANGE_TYPES)[number];

/**
 * สถานะเดียวที่ contract เอ่ยถึงตรง ๆ (ตัวอย่าง resource shape) และเป็นสถานะที่
 * คำขอ "ยังเปิดอยู่" — หน้าจอใช้ค่านี้เพื่อ **ไม่เสนอ** ปุ่มอนุมัติบนคำขอที่ถูก
 * ตัดสินไปแล้ว
 *
 * ⚠️ ไม่ได้แปลว่าค่าอื่นไม่มี: vocabulary เต็มของ `product_change_request.status`
 * ยังไม่ถูก freeze หน้าจอจึงต้องแสดงค่าที่ไม่รู้จักได้โดยไม่พัง และการซ่อนปุ่ม
 * เป็นความสะดวก ไม่ใช่ด่าน — เซิร์ฟเวอร์ตรวจ `expected_status` เองทุกคำขอ
 */
export const PRODUCT_CHANGE_REQUEST_OPEN_STATUS = 'PENDING_APPROVAL';

/**
 * คำตัดสินสองทางที่ contract freeze ไว้ — ค่าในรายการคือ path segment จริงด้วย
 * (`.../change-requests/{cr_id}/approve`) เหมือน `PRODUCT_TRANSITIONS`
 */
export const PRODUCT_CHANGE_REQUEST_DECISIONS = ['approve', 'reject'] as const;

export type ProductChangeRequestDecision = (typeof PRODUCT_CHANGE_REQUEST_DECISIONS)[number];

/**
 * ฟิลด์ระดับ product ที่เสนอแก้ได้ — ชุดเดียวกับที่ `PATCH` รับ ลบ `plant_data`
 * ออก (contract: "`proposed_changes` รับเฉพาะ field ที่ `PATCH` รับได้")
 *
 * `product_code` ไม่อยู่ในนี้เพราะ `PATCH` ไม่รับ และ `status`/ฟิลด์ผู้กระทำ
 * ไม่อยู่ในนี้เพราะ contract ตอบ `400` ให้ request ที่เอ่ยถึงมัน
 */
export const PRODUCT_CHANGE_GENERAL_FIELDS = [
  'name',
  'product_type',
  'base_uom',
  ...PRODUCT_STEEL_FIELDS,
] as const;

export type ProductChangeGeneralField = (typeof PRODUCT_CHANGE_GENERAL_FIELDS)[number];

/**
 * ฟิลด์ระดับ Plant ที่เสนอแก้ได้ — ชุดเดียวกับ `UpdateProductPlantInput` ลบ
 * `plant_code` (เป็นคีย์ที่ใช้ชี้แถว ไม่ใช่ค่าที่แก้) และลบ
 * `default_replenishment_mode` เพราะเฟส 1 เลือกได้ค่าเดียวคือ `PUSH` (D-034)
 * การเปิดช่องให้เสนอค่าที่เลือกได้ค่าเดียว คือช่องที่ไม่มีอะไรให้เสนอ
 */
export const PRODUCT_CHANGE_PLANT_FIELDS = [
  'valuation_class',
  'lot_size_procedure',
  'planned_delivery_time',
  'safety_stock',
  'unit_cost',
] as const;

export type ProductChangePlantField = (typeof PRODUCT_CHANGE_PLANT_FIELDS)[number];

/**
 * Resource shape เต็มตาม contract
 *
 * `proposed_changes` และ `baseline` เป็น map เปิดโดยตั้งใจ ไม่ผูกเป็น union ของ
 * ฟิลด์ที่รู้จัก: ทั้งคู่มาจากเซิร์ฟเวอร์ และหน้าจอต้องแสดงคีย์ที่ตัวเองยังไม่รู้จัก
 * ได้ ไม่ใช่ทำเป็นมองไม่เห็น — ข้อเสนอที่แสดงไม่ครบคือข้อเสนอที่ถูกอนุมัติโดยที่
 * ผู้อนุมัติไม่เห็นว่าตกลงอะไรไปบ้าง
 */
export interface ProductChangeRequest {
  readonly change_request_id: string;
  readonly product_id: string;
  /** `null` = ข้อเสนอระดับ product; ไม่ `null` = ข้อเสนอต่อ `product_plant_data` ของ Plant นั้น */
  readonly plant_code: string | null;
  /** เก็บเป็น string: ชนิดที่ยังไม่รู้จักต้องแสดงได้ ไม่ใช่ทำให้หน้าจอพัง */
  readonly change_type: string;
  readonly proposed_changes: Readonly<Record<string, unknown>>;
  /** ค่า ณ ตอนสร้างข้อเสนอ — **เซิร์ฟเวอร์เป็นผู้เติม client ส่งมาไม่ได้** */
  readonly baseline: Readonly<Record<string, unknown>>;
  readonly status: string;
  readonly created_by_employee_id: string | null;
  readonly approved_by_employee_id: string | null;
  readonly approved_at: string | null;
  readonly created_at: string;
  readonly updated_at: string;
}

/**
 * ผลของ `GET /v1/products/{product_id}/change-requests`
 *
 * ⚠️ contract บอกแค่ว่า endpoint นี้คืน "รายการข้อเสนอของ product นั้น" โดยไม่ได้
 * freeze envelope ว่าเป็น array เปล่า ๆ หรือ `{ items, next_cursor }` แบบ
 * `GET /v1/products` — ที่นี่จึงอ่านได้ทั้งสองแบบ (ดู `parseProductChangeRequestList`)
 * และเก็บ `next_cursor` เป็น `null` เมื่อฝั่งเซิร์ฟเวอร์ไม่ได้ส่งมา
 * เรื่องนี้ถูกรายงานกลับเป็น blocker ให้ `erp-orchestrator` ตัดสินแล้ว การเดา
 * ฝั่งเดียวแล้วบังคับให้ backend ตามคือสิ่งที่ invariant ของงานนี้ห้ามไว้
 */
export interface ProductChangeRequestListPage {
  readonly items: readonly ProductChangeRequest[];
  readonly next_cursor: string | null;
}

/**
 * Body ของ `POST /v1/products/{product_id}/change-requests` — สามคีย์ และไม่มีคีย์ที่สี่
 *
 * **ไม่มี `baseline`** โดยชนิดข้อมูล เพราะเซิร์ฟเวอร์เป็นผู้เติมจาก record จริง
 * ถ้า client ส่ง baseline เองได้ การเทียบตอน approve ก็จะเทียบกับค่าที่ผู้เสนอ
 * เขียนเอง ซึ่งทำให้ `409 stale_fields` ไร้ความหมายทั้งกลไก
 */
export interface CreateProductChangeRequestBody {
  readonly change_type: ProductChangeType;
  /** `null` = ระดับ product — `DEACTIVATE` ต้องเป็น `null` เสมอ */
  readonly plant_code: string | null;
  /** `DEACTIVATE` ต้องเป็น object ว่าง */
  readonly proposed_changes: Readonly<Record<string, unknown>>;
}

/**
 * Body ของ approve/reject — คีย์เดียวและบังคับ เหมือน transition ของ product
 * (contract: "approve และ reject รับ `expected_status` แบบเดียวกับ transition")
 */
export interface ProductChangeRequestDecisionBody {
  readonly expected_status: string;
}

/* ------------------------------------------- product type / uom register -- */

/**
 * ทะเบียน "ประเภทสินค้า" และ "หน่วยนับ" ที่หน้าสินค้าอ้างถึง (D-135 ส่วนที่ 1)
 *
 * เส้นทางจริงอยู่ที่ `router.py` บรรทัด ~1605-2010 (`product_type_router`,
 * `uom_router`) — ชนิดข้อมูลด้านล่างเป็นภาพสะท้อนของ `ProductTypeResponse`/
 * `UomResponse` เท่านั้น
 */

/**
 * สถานะของประเภทสินค้า — สองค่าเท่านั้น (`ProductTypeStatus` ฝั่งเซิร์ฟเวอร์,
 * D-115 ข้อ 4) หน่วยนับไม่มีคอลัมน์สถานะเลย จึงไม่มี union แบบนี้ให้หน่วยนับ
 */
export const PRODUCT_TYPE_STATUSES = ['ACTIVE', 'INACTIVE'] as const;

export type ProductTypeStatusValue = (typeof PRODUCT_TYPE_STATUSES)[number];

/** ความยาวสูงสุดของรหัส/ชื่อ — ตรงกับ `PRODUCT_TYPE_LENGTH`/`UOM_CODE_LENGTH`/`NAME_LENGTH` ใน `schemas.py` */
export const PRODUCT_TYPE_CODE_MAX_LENGTH = 20;
export const UOM_CODE_MAX_LENGTH = 10;
export const REGISTER_NAME_MAX_LENGTH = 120;

/** หนึ่งแถวของทะเบียนประเภทสินค้า — ไม่มี surrogate id เพราะ `type_code` เป็น primary key */
export interface ProductTypeEntry {
  readonly type_code: string;
  readonly name: string;
  /** เก็บเป็น string: สถานะที่ยังไม่รู้จักต้องแสดงได้ ไม่ใช่ทำให้หน้าจอพัง */
  readonly status: string;
  readonly created_at: string;
  readonly updated_at: string;
}

/** `GET /v1/product-types` — a page plus the cursor for the next one */
export interface ProductTypeListPage {
  readonly items: readonly ProductTypeEntry[];
  readonly next_cursor: string | null;
}

/** Body ของ `POST /v1/product-types` — ไม่มี `status` เสมอเป็น `ACTIVE` */
export interface CreateProductTypeBody {
  readonly type_code: string;
  readonly name: string;
}

/** Body ของ `PATCH /v1/product-types/{type_code}` — `name` และไม่มีคีย์ที่สอง */
export interface UpdateProductTypeBody {
  readonly name?: string;
}

/** Body ของ `.../deactivate` และ `.../activate` — คีย์เดียวและบังคับ */
export interface ProductTypeTransitionBody {
  readonly expected_status: string;
}

/** หนึ่งแถวของทะเบียนหน่วยนับ — ไม่มีคอลัมน์สถานะและไม่มี transition */
export interface UomEntry {
  readonly uom_code: string;
  readonly name: string;
  readonly created_at: string;
  readonly updated_at: string;
}

/** `GET /v1/uoms` — a page plus the cursor for the next one */
export interface UomListPage {
  readonly items: readonly UomEntry[];
  readonly next_cursor: string | null;
}

/** Body ของ `POST /v1/uoms` */
export interface CreateUomBody {
  readonly uom_code: string;
  readonly name: string;
}

/** Body ของ `PATCH /v1/uoms/{uom_code}` — `name` และไม่มีคีย์ที่สอง */
export interface UpdateUomBody {
  readonly name?: string;
}

/* --------------------------------- รูปสินค้า (D-143 ส่วนที่ 1, WO-P2-M11-025 FREEZE) --- */

/** ภาพสะท้อนของ `ProductImageResponse` ฝั่งเซิร์ฟเวอร์ — ตามที่ WO-P2-M11-026 freeze ไว้ */
export interface ProductImage {
  readonly image_id: string;
  readonly product_id: string;
  readonly filename: string;
  readonly content_type: string;
  readonly size_bytes: number;
  readonly caption: string | null;
  readonly is_primary: boolean;
  /** เก็บเป็น string: สถานะที่ยังไม่รู้จักต้องแสดงได้ ไม่ใช่ทำให้หน้าจอพัง */
  readonly status: string;
  readonly created_by_employee_id: string | null;
  readonly created_at: string;
}

/** `GET .../images` — ไม่มี cursor ตามสัญญา (คืนทุกรูป ACTIVE เรียงรูปหลักก่อน) */
export interface ProductImageListPage {
  readonly items: readonly ProductImage[];
}

/** พารามิเตอร์ของ `uploadProductImage` — `filename`/`caption` ไปทาง query string */
export interface UploadProductImageOptions {
  readonly filename: string;
  readonly caption?: string;
  readonly contentType: string;
}
